import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useNotifications, NOTIFICATION_TYPES, NOTIFICATION_PRIORITY } from '../contexts/NotificationContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Label } from './ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Bell,
  BellRing,
  CheckCheck,
  Trash2,
  Gift,
  RefreshCw,
  AlertTriangle,
  Clock,
  X,
  ChevronRight,
  Award,
  FileText,
  User,
  Settings,
  ExternalLink,
  UserPlus,
  Cake,
  CalendarCheck,
  MessageCircle,
  Instagram,
  Smartphone,
  PiggyBank,
  Eye,
  EyeOff,
  UserCheck,
} from 'lucide-react';

// Configuración de iconos y colores por tipo
const notificationConfig = {
  [NOTIFICATION_TYPES.BIRTHDAY]: {
    icon: Gift,
    label: 'Cumpleaños',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600 dark:text-pink-400',
    borderColor: 'border-l-pink-500'
  },
  [NOTIFICATION_TYPES.ANNIVERSARY]: {
    icon: Award,
    label: 'Aniversario',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-l-purple-500'
  },
  [NOTIFICATION_TYPES.POLICY_RENEWAL]: {
    icon: RefreshCw,
    label: 'Renovación',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-l-blue-500'
  },
  [NOTIFICATION_TYPES.CHARGEBACK]: {
    icon: AlertTriangle,
    label: 'Chargeback',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    borderColor: 'border-l-red-500'
  },
  [NOTIFICATION_TYPES.POLICY_PENDING]: {
    icon: Clock,
    label: 'Pendiente',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    borderColor: 'border-l-yellow-500'
  },
  [NOTIFICATION_TYPES.LEAD]: {
    icon: User,
    label: 'Lead',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-l-green-500'
  },
  [NOTIFICATION_TYPES.PAYMENT]: {
    icon: FileText,
    label: 'Pago',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-l-emerald-500'
  },
  referral_request: {
    icon: UserPlus,
    label: 'Solicitud de Registro',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    borderColor: 'border-l-indigo-500'
  },
  [NOTIFICATION_TYPES.LEAD_BIRTHDAY]: {
    icon: Cake,
    label: 'Cumpleaños Lead',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600 dark:text-pink-400',
    borderColor: 'border-l-pink-500'
  },
  [NOTIFICATION_TYPES.LEAD_FOLLOW_UP]: {
    icon: CalendarCheck,
    label: 'Seguimiento Lead',
    bgColor: 'bg-teal-100 dark:bg-teal-900/30',
    iconColor: 'text-teal-600 dark:text-teal-400',
    borderColor: 'border-l-teal-500'
  },
  [NOTIFICATION_TYPES.MESSENGER_LEAD]: {
    icon: MessageCircle,
    label: 'Lead de Messenger',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-l-blue-500'
  },
  [NOTIFICATION_TYPES.INSTAGRAM_LEAD]: {
    icon: Instagram,
    label: 'Lead de Instagram',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600 dark:text-pink-400',
    borderColor: 'border-l-pink-500'
  },
  [NOTIFICATION_TYPES.WHATSAPP_LEAD]: {
    icon: Smartphone,
    label: 'Lead de WhatsApp',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-l-green-500'
  },
  [NOTIFICATION_TYPES.APORTACION_OPLUS]: {
    icon: PiggyBank,
    label: 'Aportación OptiMaxx Plus',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-l-emerald-500'
  },
  daily_summary: {
    icon: Bell,
    label: 'Resumen del día',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    borderColor: 'border-l-indigo-500'
  },
  agent_birthday_admin: {
    icon: Gift,
    label: 'Cumpleaños de agente',
    bgColor: 'bg-sky-100 dark:bg-sky-900/30',
    iconColor: 'text-sky-600 dark:text-sky-400',
    borderColor: 'border-l-sky-500'
  },
  agent_birthday_self: {
    icon: Cake,
    label: 'Tu cumpleaños',
    bgColor: 'bg-pink-100 dark:bg-pink-900/30',
    iconColor: 'text-pink-600 dark:text-pink-400',
    borderColor: 'border-l-pink-500'
  },
  renovacion_proxima: {
    icon: RefreshCw,
    label: 'Renovación próxima',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
    borderColor: 'border-l-orange-500'
  },
  cedula_vencimiento: {
    icon: AlertTriangle,
    label: 'Cédula por vencer',
    bgColor: 'bg-violet-100 dark:bg-violet-900/30',
    iconColor: 'text-violet-600 dark:text-violet-400',
    borderColor: 'border-l-violet-500'
  },
  rc_vencimiento: {
    icon: AlertTriangle,
    label: 'RC por vencer',
    bgColor: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',
    iconColor: 'text-fuchsia-600 dark:text-fuchsia-400',
    borderColor: 'border-l-fuchsia-500'
  },
  client_birthday: {
    icon: Cake,
    label: 'Cumpleaños de cliente',
    bgColor: 'bg-rose-100 dark:bg-rose-900/30',
    iconColor: 'text-rose-600 dark:text-rose-400',
    borderColor: 'border-l-rose-500'
  },
  policy_anniversary: {
    icon: Award,
    label: 'Aniversario de póliza',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-l-amber-500'
  },
  meeting_reminder: {
    icon: CalendarCheck,
    label: 'Recordatorio de junta',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-l-purple-500'
  },
  calendar_reminder: {
    icon: CalendarCheck,
    label: 'Recordatorio',
    bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    borderColor: 'border-l-indigo-500'
  },
  cedula_rc_review_pending: {
    icon: UserCheck,
    label: 'Pendiente de aprobación',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-l-amber-500'
  },
  default: {
    icon: Bell,
    label: 'Sistema',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    iconColor: 'text-gray-600 dark:text-gray-400',
    borderColor: 'border-l-gray-500'
  }
};

const priorityBadgeColors = {
  [NOTIFICATION_PRIORITY.HIGH]: 'bg-red-500 text-white',
  [NOTIFICATION_PRIORITY.MEDIUM]: 'bg-yellow-500 text-white',
  [NOTIFICATION_PRIORITY.LOW]: 'bg-blue-500 text-white'
};

// Formatear fecha relativa
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
};

// Agrupa las pólizas de un digest de renovación por agente (nombre + clave),
// ordenando cada grupo por días restantes ASC (más urgente primero)
const groupRenovacionPolsByAgent = (pols) => {
  const groups = {};
  (pols || []).forEach((p) => {
    const key = `${p.agent_name || '—'}|${p.agent_key || '—'}`;
    if (!groups[key]) {
      groups[key] = { agent_name: p.agent_name || '—', agent_key: p.agent_key || '—', policies: [] };
    }
    groups[key].policies.push(p);
  });
  return Object.values(groups)
    .sort((a, b) => a.agent_name.localeCompare(b.agent_name))
    .map((g) => ({
      ...g,
      policies: [...g.policies].sort((a, b) => (a.days_left ?? 0) - (b.days_left ?? 0)),
    }));
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => ctx.close();
  } catch (_) {}
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearRead,
    refresh
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [approvingReactivationId, setApprovingReactivationId] = useState(null);
  const prevUnreadCount = useRef(null);

  useEffect(() => {
    if (prevUnreadCount.current !== null && unreadCount > prevUnreadCount.current) {
      playNotificationSound();
    }
    prevUnreadCount.current = unreadCount;
  }, [unreadCount]);

  // O.Plus aportacion modal state
  const [aportacionModalOpen, setAportacionModalOpen] = useState(false);
  const [aportacionNotif, setAportacionNotif] = useState(null);
  const [savingAportacion, setSavingAportacion] = useState(false);

  // Payment digest modal state
  const [paymentDigestModal, setPaymentDigestModal] = useState(false);
  const [paymentDigestData, setPaymentDigestData] = useState(null);

  // Renovacion digest modal state
  const [renovacionDigestModal, setRenovacionDigestModal] = useState(false);
  const [renovacionDigestData, setRenovacionDigestData] = useState(null);

  // Daily summary modal state
  const [dailySummaryModal, setDailySummaryModal] = useState(false);
  const [dailySummaryData, setDailySummaryData] = useState(null);

  // Lead follow-up modal state
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [followUpNotif, setFollowUpNotif] = useState(null);
  const [followUpStatus, setFollowUpStatus] = useState('contactado');
  const [followUpDate, setFollowUpDate] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);

  const [activeFilter, setActiveFilter] = useState(null);
  const [showRead, setShowRead] = useState(false);

  const handleNotificationClick = (notification) => {
    // Don't navigate on click if it's an action notification
    if (notification.actionData?.type === 'payment_confirmation') return;
    // Payment digest: open modal
    if (notification.actionData?.type === 'payment_digest') {
      setPaymentDigestData(notification.actionData);
      setPaymentDigestModal(true);
      setOpen(false);
      return;
    }
    // Renovacion digest: open modal
    if (notification.actionData?.type === 'renovacion_digest') {
      if (!notification.read) markAsRead(notification.id);
      setRenovacionDigestData(notification.actionData);
      setRenovacionDigestModal(true);
      setOpen(false);
      return;
    }
    // O.Plus aportacion: open modal
    if (notification.actionData?.type === 'aportacion_oplus') {
      setAportacionNotif(notification);
      setAportacionModalOpen(true);
      return;
    }
    // Lead follow-up: navigate to pipeline
    if (notification.actionData?.type === 'lead_follow_up') {
      if (!notification.read) markAsRead(notification.id);
      setOpen(false);
      navigate(notification.actionData?.redirect || '/pipeline');
      return;
    }
    // Lead birthday: navigate to leads
    if (notification.actionData?.type === 'lead_birthday') {
      if (!notification.read) markAsRead(notification.id);
      setOpen(false);
      navigate('/leads');
      return;
    }
    // Cedula/RC pendiente de aprobación: navega a Usuarios filtrado
    if (notification.type === 'cedula_rc_review_pending') {
      if (!notification.read) markAsRead(notification.id);
      setOpen(false);
      navigate('/users?pending_review=true');
      return;
    }
    // Daily summary digest: open in-app modal
    if (notification.actionData?.type === 'daily_summary_digest') {
      if (!notification.read) markAsRead(notification.id);
      setDailySummaryData(notification.actionData);
      setDailySummaryModal(true);
      setOpen(false);
      return;
    }
    // Daily summary (legacy — no agents payload): navigate to activity notifications tab
    if (notification.type === 'daily_summary') {
      if (!notification.read) markAsRead(notification.id);
      setOpen(false);
      const date = notification.actionData?.date || new Date().toISOString().split('T')[0];
      navigate(`/activity?tab=notifications&date=${date}`);
      return;
    }
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Navegar a la entidad relacionada
    if (notification.entityType && notification.entityId) {
      setOpen(false);
      if (notification.entityType === 'client') {
        navigate('/clients');
      } else if (notification.entityType === 'policy') {
        navigate('/policies');
      } else if (notification.entityType === 'lead') {
        navigate('/leads');
      }
    }
  };

  const handlePaymentAction = async (e, notification, action) => {
    e.stopPropagation();
    setActionLoading(`${notification.id}-${action}`);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notifications/${notification.id}/action`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refresh();
    } catch (err) {
      console.error('Error processing notification action:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmAportacion = async () => {
    if (!aportacionNotif) return;
    setSavingAportacion(true);
    try {
      const token = localStorage.getItem('token');
      const policyId = aportacionNotif.actionData?.policy_id;
      await axios.patch(
        `${API}/policies/${policyId}/aportacion`,
        { realizo_aportacion: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!aportacionNotif.read) markAsRead(aportacionNotif.id);
      setAportacionModalOpen(false);
      await refresh();
      toast.success('Aportación registrada correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar aportación');
    } finally {
      setSavingAportacion(false);
    }
  };

  const handleNoAportacion = async (e, notification) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const policyId = notification.actionData?.policy_id;
      await axios.patch(
        `${API}/policies/${policyId}/aportacion`,
        { realizo_aportacion: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!notification.read) markAsRead(notification.id);
      toast.info('Registrado: el cliente no realizó la aportación');
    } catch (err) {
      toast.error('Error al registrar');
    }
  };

  const handleApproveReactivation = async (e, notification) => {
    e.stopPropagation();
    const userId = notification.actionData?.user_id;
    if (!userId) return;
    setApprovingReactivationId(notification.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${userId}/approve-reactivation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!notification.read) markAsRead(notification.id);
      toast.success('Cuenta reactivada');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aprobar la reactivación');
    } finally {
      setApprovingReactivationId(null);
    }
  };

  const openFollowUpModal = (notification, preStatus = 'contactado') => {
    setFollowUpNotif(notification);
    setFollowUpStatus(preStatus);
    setFollowUpDate('');
    setFollowUpModalOpen(true);
  };

  const handleSaveFollowUp = async () => {
    if (!followUpNotif) return;
    setSavingFollowUp(true);
    try {
      const token = localStorage.getItem('token');
      const leadId = followUpNotif.actionData?.lead_id;
      const payload = {
        nuevo_estado: followUpStatus,
        notification_id: followUpNotif.id,
      };
      if (followUpDate && !['ganado', 'perdido'].includes(followUpStatus)) {
        payload.nueva_fecha_contacto = followUpDate;
      }
      await axios.patch(
        `${API}/leads/${leadId}/seguimiento`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFollowUpModalOpen(false);
      await refresh();
      toast.success('Seguimiento actualizado');
      if (followUpStatus === 'ganado') {
        toast.info('Lead convertido a cliente exitosamente');
      }
      setOpen(false);
      navigate('/leads');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar seguimiento');
    } finally {
      setSavingFollowUp(false);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/settings/alerts');
  };

  const getConfig = (type) => {
    return notificationConfig[type] || notificationConfig.default;
  };

  // Agrupar notificaciones por tipo para mostrar resumen
  const groupedByType = notifications.reduce((acc, notif) => {
    const type = notif.type || 'system';
    if (!acc[type]) acc[type] = [];
    acc[type].push(notif);
    return acc;
  }, {});

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative ${unreadCount > 0 ? 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30' : ''}`}
          data-testid="notification-bell"
        >
          {unreadCount > 0 ? (
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-full bg-yellow-400/50 blur-md animate-pulse" />
              <BellRing 
                className="h-5 w-5 relative z-10 animate-[pulse_1s_ease-in-out_infinite]" 
                fill="#FBBF24" 
                stroke="#F59E0B"
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <Bell 
              className="h-5 w-5 text-yellow-500 dark:text-yellow-400" 
              fill="#FCD34D" 
              stroke="#F59E0B"
              strokeWidth={1.5}
            />
          )}
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center p-0 px-1 bg-red-500 text-white text-xs font-bold animate-bounce"
              data-testid="notification-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-[420px] p-0" align="end" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#1a365d] to-[#2d4a7c] text-white rounded-t-lg">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            <h3 className="font-semibold">Notificaciones</h3>
            {unreadCount > 0 && (
              <Badge className="bg-white/20 text-white text-xs">
                {unreadCount} nueva{unreadCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowRead(v => !v)}
              className={`h-8 w-8 hover:text-white hover:bg-white/10 ${showRead ? 'text-white' : 'text-white/50'}`}
              title={showRead ? 'Ocultar leídas' : 'Mostrar leídas'}
            >
              {showRead ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={refresh}
              className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
              title="Actualizar"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={markAllAsRead}
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
                title="Marcar todas como leídas"
              >
                <CheckCheck className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        {Object.keys(groupedByType).length > 0 && (
          <div className="flex flex-wrap gap-2 p-3 border-b bg-gray-50 dark:bg-gray-800/50">
            {Object.entries(groupedByType).slice(0, 5).map(([type, items]) => {
              const config = getConfig(type);
              const Icon = config.icon;
              const unreadItems = items.filter(i => !i.read).length;
              const isActive = activeFilter === type;

              return (
                <div
                  key={type}
                  onClick={() => setActiveFilter(isActive ? null : type)}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs cursor-pointer transition-all
                    ${config.bgColor}
                    ${isActive ? 'ring-2 ring-offset-1 ring-current font-semibold' : 'opacity-70 hover:opacity-100'}`}
                >
                  <Icon className={`h-3 w-3 ${config.iconColor}`} />
                  <span className="font-medium">{config.label}</span>
                  {unreadItems > 0 && (
                    <span className="bg-white dark:bg-gray-900 px-1.5 rounded-full text-[10px] font-bold">
                      {unreadItems}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Notifications List */}
        <ScrollArea className="h-[350px]">
          {(() => {
            const displayedNotifs = notifications
              .filter(n => showRead ? true : !n.read)
              .filter(n => activeFilter ? n.type === activeFilter : true)
              .slice(0, 50);
            return displayedNotifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
                <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-3">
                  <Bell className="h-8 w-8 opacity-50" />
                </div>
                <p className="font-medium">
                  {notifications.length === 0 ? 'No hay notificaciones' : 'Sin resultados'}
                </p>
                <p className="text-xs text-center mt-1">
                  {notifications.length === 0
                    ? 'Las alertas de cumpleaños, renovaciones y más aparecerán aquí'
                    : 'Prueba cambiando el filtro o activando "Mostrar leídas"'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {displayedNotifs.map((notification) => {
                const config = getConfig(notification.type);
                const Icon = config.icon;

                return (
                  <div
                    key={notification.id}
                    className={`
                      p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50
                      transition-all duration-200 border-l-4 ${config.borderColor}
                      ${!notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}
                      ${notification.read && showRead ? 'opacity-60' : ''}
                    `}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`p-2 rounded-lg shrink-0 ${config.bgColor}`}>
                        <Icon className={`h-4 w-4 ${config.iconColor}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${config.bgColor} ${config.iconColor}`}>
                              {config.label}
                            </span>
                            {notification.priority === NOTIFICATION_PRIORITY.HIGH && (
                              <span className="text-[10px] font-bold text-red-500 uppercase">
                                Urgente
                              </span>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            className="shrink-0 p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3 text-muted-foreground" />
                          </button>
                        </div>
                        
                        <p className={`text-sm mt-1 ${!notification.read ? 'font-medium' : ''}`}>
                          {notification.title}
                        </p>
                        
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>

                        {/* Payment confirmation action buttons */}
                        {notification.actionData?.type === 'payment_confirmation' && !notification.read && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={(e) => handlePaymentAction(e, notification, 'confirm')}
                              disabled={!!actionLoading}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors"
                            >
                              {actionLoading === `${notification.id}-confirm` ? '…' : 'Sí'}
                            </button>
                            <button
                              onClick={(e) => handlePaymentAction(e, notification, 'dismiss')}
                              disabled={!!actionLoading}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded border border-border hover:bg-muted disabled:opacity-50 transition-colors"
                            >
                              {actionLoading === `${notification.id}-dismiss` ? '…' : 'No'}
                            </button>
                          </div>
                        )}

                        {/* Payment digest: botón Ver detalle */}
                        {notification.actionData?.type === 'payment_digest' && (
                          <div className="mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPaymentDigestData(notification.actionData);
                                setPaymentDigestModal(true);
                                setOpen(false);
                              }}
                              className="w-full text-xs font-medium py-1 px-2 rounded bg-[#1a365d] hover:bg-[#1a365d]/90 text-white transition-colors"
                            >
                              Ver detalle →
                            </button>
                          </div>
                        )}

                        {/* Renovacion digest: botón Ver detalle */}
                        {notification.actionData?.type === 'renovacion_digest' && (
                          <div className="mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!notification.read) markAsRead(notification.id);
                                setRenovacionDigestData(notification.actionData);
                                setRenovacionDigestModal(true);
                                setOpen(false);
                              }}
                              className="w-full text-xs font-medium py-1 px-2 rounded bg-orange-600 hover:bg-orange-700 text-white transition-colors"
                            >
                              Ver detalle →
                            </button>
                          </div>
                        )}

                        {/* O.Plus aportacion action buttons */}
                        {notification.actionData?.type === 'aportacion_oplus' && !notification.read && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAportacionNotif(notification);
                                setAportacionModalOpen(true);
                              }}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors flex items-center justify-center gap-1"
                            >
                              <PiggyBank className="h-3 w-3" />
                              Sí, realizó aportación
                            </button>
                            <button
                              onClick={(e) => handleNoAportacion(e, notification)}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded border border-border hover:bg-muted transition-colors"
                            >
                              No realizó
                            </button>
                          </div>
                        )}

                        {/* Cedula/RC pendiente de aprobación: botón inline */}
                        {notification.type === 'cedula_rc_review_pending' && !notification.read && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={(e) => handleApproveReactivation(e, notification)}
                              disabled={approvingReactivationId === notification.id}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-1"
                            >
                              <UserCheck className="h-3 w-3" />
                              {approvingReactivationId === notification.id ? 'Aprobando...' : 'Aprobar reactivación'}
                            </button>
                          </div>
                        )}

                        {/* Referral request action button */}
                        {notification.actionData?.type === 'referral_request' && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpen(false);
                                navigate('/agents');
                              }}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded bg-indigo-600 hover:bg-indigo-700 text-white transition-colors flex items-center justify-center gap-1"
                            >
                              <UserPlus className="h-3 w-3" />
                              Ver solicitud
                            </button>
                          </div>
                        )}

                        {/* Lead follow-up inline action buttons */}
                        {notification.actionData?.type === 'lead_follow_up' && !notification.read && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openFollowUpModal(notification, 'contactado');
                              }}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                            >
                              Sí, ya lo contacté
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openFollowUpModal(notification, 'nuevo');
                              }}
                              className="flex-1 text-xs font-medium py-1 px-2 rounded border border-teal-500 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                            >
                              Reprogramar
                            </button>
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-muted-foreground">
                            {formatRelativeTime(notification.createdAt)}
                          </span>

                          {notification.entityType && notification.actionData?.type !== 'payment_confirmation' && (
                            <span className="text-[10px] text-blue-600 dark:text-blue-400 flex items-center gap-0.5">
                              Ver {notification.entityType === 'client' ? 'cliente' :
                                   notification.entityType === 'policy' ? 'póliza' :
                                   notification.entityType === 'lead' ? 'lead' : 'detalle'}
                              <ChevronRight className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2 animate-pulse"></div>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            );
          })()}
        </ScrollArea>
        
        {/* Footer */}
        <div className="p-3 border-t bg-gray-50 dark:bg-gray-800/50 rounded-b-lg">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={clearRead}
              className="text-xs text-muted-foreground hover:text-red-600"
              disabled={notifications.filter(n => n.read).length === 0}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Limpiar leídas
            </Button>
            
            <Button
              onClick={handleViewAll}
              size="sm"
              className="bg-[#1a365d] hover:bg-[#1a365d]/90 text-white"
              data-testid="view-all-notifications"
            >
              <Settings className="h-3 w-3 mr-1" />
              Ver todas las notificaciones
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    {/* O.Plus Aportacion Modal */}
    <Dialog open={aportacionModalOpen} onOpenChange={setAportacionModalOpen}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PiggyBank className="h-5 w-5 text-emerald-600" />
            Confirmar aportación OptiMaxx Plus
          </DialogTitle>
          <DialogDescription>
            Confirma que el cliente realizó su aportación mensual.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 space-y-1">
            <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
              {aportacionNotif?.actionData?.client_name}
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              Póliza: <span className="font-medium">{aportacionNotif?.actionData?.policy_number}</span>
            </p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">
              Aportación{' '}
              <span className="font-bold text-sm">
                {(aportacionNotif?.actionData?.numero_aportacion_actual || 0) + 1} de 18
              </span>
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setAportacionModalOpen(false)} disabled={savingAportacion}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAportacion}
            disabled={savingAportacion}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {savingAportacion ? 'Guardando...' : 'Confirmar aportación'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Payment Digest Modal */}
    <Dialog open={paymentDigestModal} onOpenChange={setPaymentDigestModal}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            💳 Pólizas pendientes de pago
          </DialogTitle>
          <DialogDescription>
            {paymentDigestData?.total ?? 0} póliza{(paymentDigestData?.total ?? 0) !== 1 ? 's' : ''} requieren atención
          </DialogDescription>
        </DialogHeader>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {paymentDigestData?.agents ? (
            /* Nueva estructura: agrupado por agente */
            (paymentDigestData.agents || []).map((agentGroup, gi) => (
              <div key={gi} style={{ marginBottom: '16px' }}>
                <div style={{ background: '#1a365d', color: 'white', padding: '6px 12px', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>
                  {agentGroup.agent_label}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#e2e8f0' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>Emisor</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>Póliza #</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>Cliente</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left' }}>Período</th>
                      <th style={{ padding: '4px 8px', textAlign: 'center' }}>Días retraso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(agentGroup.policies || []).map((pol, pi) => {
                      const dias = pol.dias_retraso || 0;
                      const diasColor = dias > 0 ? '#dc2626' : '#16a34a';
                      const diasText = dias > 0 ? `+${dias} días` : 'Al corriente';
                      return (
                        <tr key={pi} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '4px 8px' }}>{pol.emisor}</td>
                          <td style={{ padding: '4px 8px', fontWeight: '600' }}>{pol.policy_number}</td>
                          <td style={{ padding: '4px 8px' }}>{pol.client_name}</td>
                          <td style={{ padding: '4px 8px' }}>{pol.periodo || '—'}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'center', color: diasColor, fontWeight: 'bold' }}>{diasText}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          ) : (
            /* Estructura antigua fallback: lista flat */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#1a365d', color: 'white', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Clave</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Agente</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Emisor</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Póliza #</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Cliente</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Período</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Días retraso</th>
                </tr>
              </thead>
              <tbody>
                {(paymentDigestData?.policies || []).map((pol, idx) => {
                  const diasColor = pol.dias_retraso > 0 ? '#dc2626' : '#16a34a';
                  const diasText = pol.dias_retraso > 0 ? `+${pol.dias_retraso} días` : 'Al corriente';
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '4px 8px', color: '#6b7280', fontSize: '11px' }}>{pol.agent_key || '—'}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.agent_name || '—'}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.emisor}</td>
                      <td style={{ padding: '4px 8px', fontWeight: '600' }}>{pol.policy_number}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.client_name}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.periodo || '—'}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', color: diasColor, fontWeight: 'bold' }}>{diasText}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => setPaymentDigestModal(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Renovacion Digest Modal */}
    <Dialog open={renovacionDigestModal} onOpenChange={setRenovacionDigestModal}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-orange-600" />
            🔔 Pólizas próximas a vencer
          </DialogTitle>
          <DialogDescription>
            {renovacionDigestData?.total ?? 0} póliza{(renovacionDigestData?.total ?? 0) !== 1 ? 's' : ''} próxima{(renovacionDigestData?.total ?? 0) !== 1 ? 's' : ''} a renovar
          </DialogDescription>
        </DialogHeader>
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {groupRenovacionPolsByAgent(renovacionDigestData?.pols).map((agentGroup, gi) => (
            <div key={gi} style={{ marginBottom: '16px' }}>
              <div style={{ background: '#1a365d', color: 'white', padding: '6px 12px', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>
                {agentGroup.agent_name} ({agentGroup.agent_key})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#e2e8f0' }}>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Emisor</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Póliza #</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Cliente</th>
                    <th style={{ padding: '4px 8px', textAlign: 'left' }}>Fecha vencimiento</th>
                    <th style={{ padding: '4px 8px', textAlign: 'center' }}>Días restantes</th>
                  </tr>
                </thead>
                <tbody>
                  {agentGroup.policies.map((pol, pi) => (
                    <tr key={pi} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '4px 8px' }}>{pol.emisor || '—'}</td>
                      <td style={{ padding: '4px 8px', fontWeight: '600' }}>{pol.policy_number}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.client_name}</td>
                      <td style={{ padding: '4px 8px' }}>{pol.end_date}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'center', fontWeight: 'bold' }}>
                        Faltan {pol.days_left} día{pol.days_left !== 1 ? 's' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => setRenovacionDigestModal(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Lead Follow-Up Modal */}
    <Dialog open={followUpModalOpen} onOpenChange={setFollowUpModalOpen}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            Actualizar seguimiento de {followUpNotif?.actionData?.lead_name}
          </DialogTitle>
          <DialogDescription>
            Actualiza el estado del lead y programa la próxima fecha de contacto.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Estado del lead</Label>
            <Select value={followUpStatus} onValueChange={setFollowUpStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nuevo">Nuevo</SelectItem>
                <SelectItem value="contactado">Contactado</SelectItem>
                <SelectItem value="en_negociacion">En negociación</SelectItem>
                <SelectItem value="ganado">Ganado</SelectItem>
                <SelectItem value="perdido">Perdido</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {followUpStatus === 'ganado' && (
            <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300">
              ✅ Este lead será convertido a cliente automáticamente.
            </div>
          )}

          {!['ganado', 'perdido'].includes(followUpStatus) && (
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Nueva fecha de contacto (opcional)</Label>
              <input
                id="follow-up-date"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setFollowUpModalOpen(false)} disabled={savingFollowUp}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveFollowUp}
            disabled={savingFollowUp}
            className="bg-[#1a365d] hover:bg-[#1a365d]/90"
          >
            {savingFollowUp ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    {/* Daily Summary Modal */}
    <Dialog open={dailySummaryModal} onOpenChange={setDailySummaryModal}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-indigo-600" />
            📋 Resumen del día — {dailySummaryData?.date}
          </DialogTitle>
          <DialogDescription>
            {dailySummaryData?.total ?? 0} notificación{(dailySummaryData?.total ?? 0) !== 1 ? 'es' : ''} enviada{(dailySummaryData?.total ?? 0) !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {(dailySummaryData?.agents || []).map((ag) => {
            const allRows = Object.values(ag.by_product || {})
              .flat()
              .sort((a, b) => (b.dias_retraso || 0) - (a.dias_retraso || 0));
            return (
              <div key={ag.agent_id} style={{ marginBottom: '16px' }}>
                <h4 style={{ color: '#1a365d', margin: '0 0 6px', fontWeight: 600, fontSize: '14px' }}>
                  {ag.agent_name}
                  {ag.agent_key && ag.agent_key !== '—' ? ` (${ag.agent_key})` : ''}
                  <span style={{ fontWeight: 400, fontSize: '12px', color: '#718096', marginLeft: '6px' }}>
                    ({ag.count})
                  </span>
                </h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#edf2f7' }}>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Tipo</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Emisor</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Póliza #</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Cliente</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Período</th>
                      <th style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #e2e8f0' }}>Días retraso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((r, i) => {
                      const dias = r.dias_retraso;
                      const diasCell = dias === null || dias === undefined
                        ? '—'
                        : dias > 0
                          ? <span style={{ color: '#dc2626', fontWeight: 600 }}>+{dias} días</span>
                          : <span style={{ color: '#16a34a' }}>Al corriente</span>;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{r.type}</td>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{r.emisor || '—'}</td>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{r.policy_number}</td>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{r.client_name}</td>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{r.periodo}</td>
                          <td style={{ padding: '3px 8px', border: '1px solid #e2e8f0' }}>{diasCell}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => setDailySummaryModal(false)}>
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default NotificationBell;
